import React, { useState } from 'react';
import {
  Box,
  HStack,
  Input,
  Icon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  VStack,
  Select,
  Grid,
  GridItem,
  useDisclosure,
  Divider,
  Text,
} from '@chakra-ui/react';
import { FiSearch, FiFilter } from 'react-icons/fi';

const GlobalInventoryFilter = ({ onFilter, onSearch, loading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Advanced filter state
  const [advancedFilters, setAdvancedFilters] = useState({
    start_date: '',
    end_date: '',
    width: '',
    depth: '',
    floors: '',
    product_code: '',
    status: '',
    created_by: '',
    category: '',
  });

  // Handle basic search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Call search with debounce effect
    setTimeout(() => {
      onSearch(value);
    }, 500);
  };

  // Handle advanced filter input changes
  const handleAdvancedFilterChange = (field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply advanced filters
  const handleApplyFilters = () => {
    onFilter(advancedFilters);
    onClose();
  };

  // Reset all filters
  const handleResetFilters = () => {
    const resetFilters = {
      start_date: '',
      end_date: '',
      width: '',
      depth: '',
      floors: '',
      product_code: '',
      status: '',
      created_by: '',
      category: '',
    };
    setAdvancedFilters(resetFilters);
    setSearchTerm('');
    onFilter(resetFilters);
    onSearch('');
    onClose();
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <>
      {/* Filter Controls */}
      <HStack spacing={4} align="center">
        {/* Basic Search */}
        <Box position="relative">
          <Input
            placeholder="Search products..."
            size="sm"
            width="200px"
            pl={8}
            bg="#FAFBFC"
            border="1px"
            borderColor="#DFE1E6"
            borderRadius="4px"
            fontSize="14px"
            value={searchTerm}
            onChange={handleSearchChange}
            _focus={{
              borderColor: "#0065FF",
              boxShadow: "0 0 0 1px #0065FF",
            }}
          />
          <Icon
            as={FiSearch}
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            color="#6B778C"
            boxSize="14px"
          />
        </Box>

        {/* Advanced Filter Button */}
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Icon as={FiFilter} />}
          onClick={onOpen}
          colorScheme="blue"
        >
          Advanced Filter
        </Button>
      </HStack>

      {/* Advanced Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="blue.600" color="white" borderTopRadius="md">
            <HStack>
              <Icon as={FiFilter} />
              <Text>Advanced Filters</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          
          <ModalBody py={6}>
            <VStack spacing={6} align="stretch">
              
              {/* Date Range Section */}
              <Box>
                <Text fontWeight="600" mb={3} color="gray.700">Date Range</Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Start Date</FormLabel>
                      <Input
                        type="date"
                        value={advancedFilters.start_date}
                        max={getTodayDate()}
                        onChange={(e) => handleAdvancedFilterChange('start_date', e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">End Date</FormLabel>
                      <Input
                        type="date"
                        value={advancedFilters.end_date}
                        max={getTodayDate()}
                        onChange={(e) => handleAdvancedFilterChange('end_date', e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              <Divider />

              {/* Product Specifications */}
              <Box>
                <Text fontWeight="600" mb={3} color="gray.700">Product Specifications</Text>
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Width</FormLabel>
                      <Input
                        placeholder="e.g., 30"
                        value={advancedFilters.width}
                        onChange={(e) => handleAdvancedFilterChange('width', e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Depth</FormLabel>
                      <Input
                        placeholder="e.g., 50"
                        value={advancedFilters.depth}
                        onChange={(e) => handleAdvancedFilterChange('depth', e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Floors</FormLabel>
                      <Select
                        placeholder="Select floors"
                        value={advancedFilters.floors}
                        onChange={(e) => handleAdvancedFilterChange('floors', e.target.value)}
                        size="sm"
                      >
                        <option value="1">1 Floor</option>
                        <option value="2">2 Floors</option>
                        <option value="3">3 Floors</option>
                        <option value="4">4+ Floors</option>
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              <Divider />

              {/* Product Details */}
              <Box>
                <Text fontWeight="600" mb={3} color="gray.700">Product Details</Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Product Code</FormLabel>
                      <Input
                        placeholder="Enter product code"
                        value={advancedFilters.product_code}
                        onChange={(e) => handleAdvancedFilterChange('product_code', e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Category</FormLabel>
                      <Select
                        placeholder="Select category"
                        value={advancedFilters.category}
                        onChange={(e) => handleAdvancedFilterChange('category', e.target.value)}
                        size="sm"
                      >
                        <option value="floorplan">Floorplan</option>
                        <option value="architectural">Architectural</option>
                        <option value="elevation">Elevation</option>
                        <option value="electrical">Electrical</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="drainage">Drainage</option>
                        <option value="2d">2D</option>
                        <option value="3d">3D</option>
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              <Divider />

              {/* Status and Creator */}
              <Box>
                <Text fontWeight="600" mb={3} color="gray.700">Status & Creator</Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Status</FormLabel>
                      <Select
                        placeholder="Select status"
                        value={advancedFilters.status}
                        onChange={(e) => handleAdvancedFilterChange('status', e.target.value)}
                        size="sm"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                        <option value="draft">Draft</option>
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Created By</FormLabel>
                      <Input
                        placeholder="Creator name"
                        value={advancedFilters.created_by}
                        onChange={(e) => handleAdvancedFilterChange('created_by', e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

            </VStack>
          </ModalBody>

          <ModalFooter bg="gray.50" borderBottomRadius="md">
            <HStack spacing={3}>
              <Button
                variant="outline"
                onClick={handleResetFilters}
                size="sm"
              >
                Reset All
              </Button>
              <Button
                variant="ghost"
                onClick={onClose}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleApplyFilters}
                size="sm"
                isLoading={loading}
              >
                Apply Filters
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default GlobalInventoryFilter;